import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CurrencyPolicyRecord, FinanceApiService } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-control-center-currency-policies',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('finance.multiCurrency.policies.title')" [description]="t('finance.multiCurrency.policies.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('common.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('catalog.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelectedDetail()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>

          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('catalog.filter.hide') : t('catalog.filter.show')" severity="secondary" (click)="toggleFilters()"></button>
          </div>

          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('finance.multiCurrency.policies.functionalCurrency') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.functionalCurrencyCode" (ngModelChange)="applyFilters(true)" />
              </label>
              <label>
                <span>{{ t('finance.multiCurrency.policies.reportingCurrency') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.reportingCurrencyCode" (ngModelChange)="applyFilters(true)" />
              </label>
              <label>
                <span>{{ t('finance.multiCurrency.policies.requireApproval') }}</span>
                <input pInputText type="text" [(ngModel)]="filters.requireApproval" (ngModelChange)="applyFilters(true)" />
              </label>
            </div>
          }

          @if (!rows().length) {
            <ain-state type="empty" [title]="t('finance.multiCurrency.policies.emptyTitle')" />
          } @else {
            <p-table
              [value]="rows()"
              dataKey="id"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('finance.multiCurrency.policies.functionalCurrency') }}</th>
                  <th>{{ t('finance.multiCurrency.policies.reportingCurrency') }}</th>
                  <th>{{ t('finance.multiCurrency.policies.requireApproval') }}</th>
                  <th>{{ t('finance.multiCurrency.policies.allowCrossPosting') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>
                    <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.functionalCurrencyCode }}</button>
                  </td>
                  <td>{{ row.reportingCurrencyCode }}</td>
                  <td>{{ row.requireFxRateApproval ? t('common.yes') : t('common.no') }}</td>
                  <td>{{ row.allowCrossCurrencyPosting ? t('common.yes') : t('common.no') }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; justify-content: flex-start; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .filters span { font-size: 12px; color: #475569; font-weight: 600; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class FinanceControlCenterCurrencyPoliciesComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly showFilters = signal(true);
  readonly sourceRows = signal<CurrencyPolicyRecord[]>([]);
  readonly rows = signal<CurrencyPolicyRecord[]>([]);
  readonly selectedRows = signal<CurrencyPolicyRecord[]>([]);

  readonly filters = {
    functionalCurrencyCode: '',
    reportingCurrencyCode: '',
    requireApproval: ''
  };

  ngOnInit(): void {
    this.filters.functionalCurrencyCode = this.route.snapshot.queryParamMap.get('functionalCurrencyCode') ?? '';
    this.filters.reportingCurrencyCode = this.route.snapshot.queryParamMap.get('reportingCurrencyCode') ?? '';
    this.filters.requireApproval = this.route.snapshot.queryParamMap.get('requireApproval') ?? '';
    this.showFilters.set((this.route.snapshot.queryParamMap.get('showFilters') ?? '1') !== '0');
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.getCurrencyPolicy().subscribe({
      next: row => {
        this.sourceRows.set([row]);
        this.applyFilters(false);
        this.loading.set(false);
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          this.sourceRows.set([]);
          this.rows.set([]);
          this.loading.set(false);
          return;
        }

        this.sourceRows.set([]);
        this.rows.set([]);
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  toggleFilters(): void {
    this.showFilters.set(!this.showFilters());
    this.syncQueryParams();
  }

  applyFilters(syncQueryParams: boolean): void {
    const functionalCurrencyCode = this.filters.functionalCurrencyCode.trim().toLowerCase();
    const reportingCurrencyCode = this.filters.reportingCurrencyCode.trim().toLowerCase();
    const requireApproval = this.filters.requireApproval.trim().toLowerCase();

    const filtered = this.sourceRows().filter(item => {
      const requireApprovalLabel = item.requireFxRateApproval ? this.t('common.yes').toLowerCase() : this.t('common.no').toLowerCase();
      return (!functionalCurrencyCode || item.functionalCurrencyCode.toLowerCase().includes(functionalCurrencyCode))
        && (!reportingCurrencyCode || item.reportingCurrencyCode.toLowerCase().includes(reportingCurrencyCode))
        && (!requireApproval || requireApprovalLabel.includes(requireApproval));
    });

    this.rows.set(filtered);
    const selectedIds = new Set(this.selectedRows().map(item => item.id));
    this.selectedRows.set(filtered.filter(item => selectedIds.has(item.id)));
    if (syncQueryParams) {
      this.syncQueryParams();
    }
  }

  onSelectionChange(value: CurrencyPolicyRecord[]): void {
    this.selectedRows.set(value ?? []);
  }

  openCreate(): void {
    void this.router.navigate(['/financial/control-center/currency-policies', 'new'], { queryParams: this.currentQueryParams() });
  }

  openDetail(id: string): void {
    void this.router.navigate(['/financial/control-center/currency-policies', id], { queryParams: this.currentQueryParams() });
  }

  openSelectedDetail(): void {
    const selected = this.selectedRows();
    if (selected.length === 1) {
      this.openDetail(selected[0].id);
    }
  }

  private syncQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.currentQueryParams(),
      replaceUrl: true
    });
  }

  private currentQueryParams(): Record<string, string> {
    return {
      functionalCurrencyCode: this.filters.functionalCurrencyCode.trim(),
      reportingCurrencyCode: this.filters.reportingCurrencyCode.trim(),
      requireApproval: this.filters.requireApproval.trim(),
      showFilters: this.showFilters() ? '1' : '0'
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
